// Equipment catalog for TinkerShift — makerspace recommendations.
// Grouped by category. Each item has a tier (Essential / Core / Advanced),
// an approximate AUD price band, and a short rationale.

export const EQUIPMENT_CATEGORIES = [
  {
    key: "3d_printing",
    name: "3D Printing",
    accent: "#FFD700",
    icon: "Boxes",
    items: [
      {
        name: "Bambu Lab A1 / P1S",
        tier: "Essential",
        price: "$900 – $1,400",
        blurb:
          "Reliable, fast FDM printer. Low-fuss slicer, auto bed-levelling. The workhorse for every makerspace.",
      },
      {
        name: "Spare filament kit (PLA, PETG, TPU)",
        tier: "Essential",
        price: "$150 – $300",
        blurb:
          "A rotating stock of the three essentials so students never wait on a print.",
      },
      {
        name: "Resin printer (Anycubic Mono / Elegoo Saturn)",
        tier: "Advanced",
        price: "$500 – $900",
        blurb:
          "For high-detail miniatures, dental-grade accuracy and jewellery-scale prototyping.",
      },
    ],
  },
  {
    key: "cutting",
    name: "Cutting & Engraving",
    accent: "#FF007F",
    icon: "Scissors",
    items: [
      {
        name: "Glowforge / xTool P2 laser cutter",
        tier: "Core",
        price: "$5,000 – $9,000",
        blurb:
          "The single most game-changing tool for a school makerspace — turns ideas into physical artefacts in minutes.",
      },
      {
        name: "Cricut Maker 3 vinyl cutter",
        tier: "Essential",
        price: "$500 – $700",
        blurb:
          "Signage, stickers, iron-on branding, paper engineering. Low barrier, huge impact.",
      },
      {
        name: "Scroll saw + bench vice",
        tier: "Essential",
        price: "$300 – $600",
        blurb:
          "The low-tech counterpoint — teaches real hand-skills, safely.",
      },
    ],
  },
  {
    key: "electronics",
    name: "Electronics",
    accent: "#0055FF",
    icon: "CircuitBoard",
    items: [
      {
        name: "micro:bit v2 class kit",
        tier: "Essential",
        price: "$600 – $900",
        blurb:
          "The best entry point. 30-pack with accessories covers a whole year group.",
      },
      {
        name: "Arduino Starter kits (×10)",
        tier: "Core",
        price: "$1,000 – $1,500",
        blurb:
          "Where learning jumps from blocks to C/C++. Durable, documented, reusable.",
      },
      {
        name: "Soldering stations (×6) + fume extractor",
        tier: "Core",
        price: "$800 – $1,400",
        blurb:
          "Real electronics work with real safety. Temperature-controlled, ESD-safe.",
      },
      {
        name: "Component library (resistors, LEDs, caps, sensors)",
        tier: "Essential",
        price: "$300 – $600",
        blurb:
          "Organised in labelled drawers — students self-serve, you refill once a term.",
      },
    ],
  },
  {
    key: "robotics",
    name: "Robotics",
    accent: "#00C2B8",
    icon: "Bot",
    items: [
      {
        name: "Sphero Bolt / Indi class pack",
        tier: "Essential",
        price: "$1,500 – $2,500",
        blurb:
          "Perfect for 5–8s: visual coding, immediate feedback, strong curriculum support.",
      },
      {
        name: "LEGO SPIKE Prime (×6 sets)",
        tier: "Core",
        price: "$3,500 – $5,000",
        blurb:
          "The gold standard for middle years. Covers mechanics, coding and problem solving.",
      },
      {
        name: "VEX IQ Gen 2 competition kits",
        tier: "Advanced",
        price: "$4,000 – $7,000",
        blurb:
          "For schools going into competition circuits. Pathways to RoboCup / Vex Worlds.",
      },
    ],
  },
  {
    key: "media",
    name: "Media & Creativity",
    accent: "#FF6600",
    icon: "Video",
    items: [
      {
        name: "Green-screen + lighting kit",
        tier: "Core",
        price: "$400 – $900",
        blurb:
          "Essential for stop-motion, animation and student podcasts/presentations.",
      },
      {
        name: "Rode / Shure USB microphones (×4)",
        tier: "Essential",
        price: "$600 – $1,000",
        blurb:
          "Podcasting, voiceovers, interviews. Massively underused in schools.",
      },
      {
        name: "iPad Pro + Apple Pencil (×6)",
        tier: "Advanced",
        price: "$12,000 – $15,000",
        blurb:
          "For digital art, Procreate, Keynote animation and portfolio capture.",
      },
    ],
  },
  {
    key: "safety",
    name: "Safety & Space",
    accent: "#1A1A1A",
    icon: "ShieldCheck",
    items: [
      {
        name: "Safety glasses + apron station",
        tier: "Essential",
        price: "$200 – $400",
        blurb:
          "Non-negotiable. Visible = used.",
      },
      {
        name: "First-aid + burn station",
        tier: "Essential",
        price: "$150 – $300",
        blurb:
          "Positioned next to the laser cutter and soldering stations.",
      },
      {
        name: "Mobile workbench trolleys (×4)",
        tier: "Core",
        price: "$1,200 – $2,000",
        blurb:
          "Reconfigurable layouts beat fixed-furniture labs every time.",
      },
      {
        name: "Pegboards + tool shadow boards",
        tier: "Essential",
        price: "$300 – $700",
        blurb:
          "Everything has a home. Students pack down in 90 seconds.",
      },
    ],
  },
];

export const EQUIPMENT_TIERS = {
  Essential: { color: "#00C2B8", desc: "Start here — the baseline for any space." },
  Core: { color: "#FFD700", desc: "Expand into — a confident, flexible makerspace." },
  Advanced: { color: "#FF007F", desc: "Go deep — competition, pathways, specialist streams." },
};

// Interactive inventory checklist. Flat list grouped by category.
export const CHECKLIST_SECTIONS = [
  {
    key: "basics",
    name: "Basic tools",
    items: [
      "Pegboards / tool shadow boards",
      "Mobile workbenches / trolleys",
      "Storage bins labelled by category",
      "Safety glasses (class set)",
      "First-aid + burn station",
      "Fire extinguisher within reach",
    ],
  },
  {
    key: "3d_printing",
    name: "3D printing",
    items: [
      "FDM 3D printer (Bambu / Prusa / similar)",
      "Spare filament stock (PLA / PETG / TPU)",
      "Resin printer + wash station",
      "Build-plate release tools",
    ],
  },
  {
    key: "cutting",
    name: "Cutting & engraving",
    items: [
      "Laser cutter (Glowforge / xTool / similar)",
      "Laser-safe material stock",
      "Vinyl cutter (Cricut / Silhouette)",
      "Scroll saw / bench saw",
      "Rulers, cutting mats, craft knives",
    ],
  },
  {
    key: "electronics",
    name: "Electronics",
    items: [
      "micro:bit class pack",
      "Arduino / Raspberry Pi kits",
      "Soldering stations + fume extractor",
      "Multimeters",
      "Breadboards + jumper wires",
      "Organised component library",
    ],
  },
  {
    key: "robotics",
    name: "Robotics",
    items: [
      "Sphero / Indi (lower primary)",
      "LEGO SPIKE Prime (middle years)",
      "VEX / Dash / Edison",
      "Charging dock(s) for class sets",
    ],
  },
  {
    key: "media",
    name: "Media & creative",
    items: [
      "Green-screen + lighting",
      "USB / lavalier microphones",
      "DSLR or mirrorless camera",
      "iPad / tablet class set",
      "Design software access (Fusion, Canva, Procreate)",
    ],
  },
  {
    key: "space",
    name: "Space & culture",
    items: [
      "Flexible reconfigurable layout",
      "Showcase / display shelving for student work",
      "Clear 'make / mess / clean' zones",
      "Timetabled bookable sessions",
      "Student-led facilitator roster",
    ],
  },
];

export const FACILITATOR_TIERS = [
  {
    lookup_key: "facilitator_foundation",
    name: "Foundation Facilitator",
    tagline: "Entry-level. For teachers new to makerspaces.",
    duration: "1-day workshop + self-paced resources",
    color: "#FFD700",
    text: "#1A1A1A",
    outcomes: [
      "Confident with core makerspace tools",
      "Introductory session planning",
      "Safety protocols & setup",
      "Entry-level TinkerShift credential",
    ],
  },
  {
    lookup_key: "facilitator_certified",
    name: "Certified Facilitator",
    tagline: "The real certification. For working educators.",
    duration: "3-day cohort workshop + assessment",
    color: "#FF007F",
    text: "#FFFFFF",
    highlight: true,
    outcomes: [
      "Everything in Foundation, plus:",
      "Project scaffolding & PBL design",
      "Equipment training across the stack",
      "Formal assessment + badge",
      "Listed in TinkerShift facilitator directory",
    ],
  },
  {
    lookup_key: "facilitator_lead",
    name: "Lead Facilitator",
    tagline: "For heads of makerspace, STEAM leads, library leads.",
    duration: "6-day cohort + mentoring + capstone",
    color: "#0055FF",
    text: "#FFFFFF",
    outcomes: [
      "Everything in Certified, plus:",
      "Strategy, roadmap and budget planning",
      "Train-the-trainer skills",
      "Capstone showcase at your site",
      "12 months mentoring with Julija",
    ],
  },
];
